import { ServiceLogo } from "@/components/ServiceLogo";
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, PlusCircle, SlidersHorizontal, Gift, Megaphone, X, Bell } from "lucide-react";
import AnnonceCard from "@/components/annonces/AnnonceCard";

export default function AnnoncesList() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [q, setQ] = useState(sp.get("q") || "");
  const [city, setCity] = useState(sp.get("city") || "");
  const [priceMin, setPriceMin] = useState(sp.get("price_min") || "");
  const [priceMax, setPriceMax] = useState(sp.get("price_max") || "");
  const [freeOnly, setFreeOnly] = useState(sp.get("free") === "1");
  const [radius, setRadius] = useState("");
  const [sort, setSort] = useState(sp.get("sort") || "recent");
  const [page, setPage] = useState(1);

  const category = slug || "";
  const activeCat = cats.find((c) => c.slug === category);

  useEffect(() => {
    api.get("/annonces/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/annonces/communes").then((r) => setCommunes(r.data?.communes || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      if (city && radius) { params.set("near", city); params.set("radius", radius); }
      else if (city) params.set("city", city);
      if (priceMin) params.set("price_min", priceMin);
      if (priceMax) params.set("price_max", priceMax);
      if (freeOnly) params.set("free_only", "true");
      params.set("sort", sort);
      params.set("page", String(page));
      const { data } = await api.get(`/annonces?${params.toString()}`);
      setData(data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [q, category, city, priceMin, priceMax, freeOnly, sort, radius, page]);

  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [category, q, city, priceMin, priceMax, freeOnly, sort]);

  useSeo({
    title: activeCat
      ? `${activeCat.name} — Petites annonces en Eure-et-Loir | PROXIreduc-28`
      : "Petites annonces en Eure-et-Loir | PROXIreduc-28",
    description: "Découvrez les petites annonces près de chez vous à Chartres et partout en Eure-et-Loir. Achetez, vendez, donnez et trouvez localement, gratuitement.",
    canonicalPath: category ? `/petites-annonces/categorie/${category}` : "/petites-annonces",
  });

  const resetFilters = () => { setQ(""); setCity(""); setPriceMin(""); setPriceMax(""); setFreeOnly(false); setSort("recent"); setRadius(""); setPage(1); setSp({}); };
  const hasFilters = q || city || priceMin || priceMax || freeOnly;

  const createAlert = async () => {
    if (!user || user === false) { toast.message("Connectez-vous pour créer une alerte."); navigate("/connexion"); return; }
    try {
      await api.post("/me/saved-searches", { q, category, city: (city && radius) ? "" : city, price_min: priceMin || null, price_max: priceMax || null, free_only: freeOnly, near: (city && radius) ? city : "", radius: radius ? Number(radius) : null });
      toast.success("Alerte créée ! Vous recevrez un résumé quotidien par e-mail.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="annonces-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-start gap-4">
          <ServiceLogo serviceKey="petites-annonces" size={64} className="mt-1" />
          <div>
          <div className="inline-flex items-center gap-2 text-[#E05638] font-semibold text-sm"><Megaphone size={16} /> PROXIreduc-28</div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">
            {activeCat ? activeCat.name : "Petites annonces en Eure-et-Loir"}
          </h1>
          <p className="mt-2 text-slate-600 text-sm md:text-base max-w-3xl">
            Découvrez les annonces près de chez vous à Chartres, dans son agglomération et partout en Eure-et-Loir.
            Trouvez un objet d'occasion, donnez ce dont vous n'avez plus besoin ou publiez gratuitement votre annonce locale.
          </p>
          </div>
        </div>
        <Link to="/petites-annonces/deposer" data-testid="annonces-deposit-btn"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl btn-shine text-white font-semibold shrink-0">
          <PlusCircle size={17} /> Déposer une annonce
        </Link>
      </div>

      {/* Catégories */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="annonces-categories">
        <Link to="/petites-annonces" data-testid="annonces-cat-all"
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${!category ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
          Toutes
        </Link>
        {cats.map((c) => (
          <Link key={c.slug} to={`/petites-annonces/categorie/${c.slug}`} data-testid={`annonces-cat-${c.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5 ${category === c.slug ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
            {c.name}{c.count > 0 && <span className={`text-[11px] ${category === c.slug ? "text-white/80" : "text-slate-400"}`}>{c.count}</span>}
          </Link>
        ))}
      </div>

      {/* Recherche + filtres */}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-3 sm:p-4" data-testid="annonces-search-bar">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
                   placeholder="Rechercher un objet, une marque…" className="pl-9" data-testid="annonces-search-input" />
          </div>
          <Button variant="outline" onClick={() => setShowFilters((v) => !v)} data-testid="annonces-filters-toggle" className="shrink-0">
            <SlidersHorizontal size={15} className="mr-1.5" /> Filtres
          </Button>
          <Button onClick={() => { setPage(1); load(); }} className="btn-shine text-white shrink-0" data-testid="annonces-search-submit">Rechercher</Button>
        </div>

        {showFilters && (
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-slate-100" data-testid="annonces-filters">
            <div>
              <label className="text-xs font-medium text-slate-600">Commune</label>
              <Select value={city || "all"} onValueChange={(v) => { setCity(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="annonces-filter-city"><SelectValue placeholder="Toutes les communes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les communes</SelectItem>
                  {communes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Rayon autour de la commune</label>
              <Select value={radius || "0"} onValueChange={(v) => { setRadius(v === "0" ? "" : v); setPage(1); }} disabled={!city}>
                <SelectTrigger data-testid="annonces-filter-radius"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Commune exacte</SelectItem>
                  <SelectItem value="10">+ 10 km autour</SelectItem>
                  <SelectItem value="20">+ 20 km autour</SelectItem>
                  <SelectItem value="30">+ 30 km autour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Prix min (€)</label>
              <Input type="number" min="0" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="0" data-testid="annonces-filter-pmin" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Prix max (€)</label>
              <Input type="number" min="0" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="1000" data-testid="annonces-filter-pmax" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Trier par</label>
              <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
                <SelectTrigger data-testid="annonces-filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Plus récentes</SelectItem>
                  <SelectItem value="price_asc">Prix croissant</SelectItem>
                  <SelectItem value="price_desc">Prix décroissant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer sm:col-span-2">
              <input type="checkbox" checked={freeOnly} onChange={(e) => { setFreeOnly(e.target.checked); setPage(1); }} data-testid="annonces-filter-free" />
              <Gift size={14} className="text-emerald-600" /> Uniquement les dons / objets gratuits
            </label>
            {hasFilters && (
              <button onClick={resetFilters} className="text-sm text-slate-500 hover:text-[#E05638] inline-flex items-center gap-1 self-end" data-testid="annonces-filter-reset">
                <X size={13} /> Réinitialiser
              </button>
            )}
          </div>
        )}
      </div>

      {/* Résultats */}
      <div className="mt-6">
        <div className="text-sm text-slate-500 mb-3 flex items-center justify-between gap-2" data-testid="annonces-count">
          <span>{loading ? "Chargement…" : `${data.total} annonce${data.total > 1 ? "s" : ""}`}</span>
          <button onClick={createAlert} className="inline-flex items-center gap-1 text-[#E05638] font-semibold hover:underline shrink-0" data-testid="annonces-create-alert">
            <Bell size={14} /> Créer une alerte
          </button>
        </div>
        {!loading && data.items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="annonces-empty">
            <Megaphone className="mx-auto text-slate-300" size={40} />
            <div className="mt-2 text-slate-600 font-semibold">Aucune annonce ne correspond.</div>
            <p className="mt-1 text-sm text-slate-500">Élargissez votre recherche ou publiez la première annonce de votre commune.</p>
            <Link to="/petites-annonces/deposer" className="mt-4 inline-flex items-center gap-1 btn-shine text-white font-semibold px-5 py-2.5 rounded-xl">
              <PlusCircle size={16} /> Déposer une annonce
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="annonces-grid">
            {data.items.map((a) => <AnnonceCard key={a.id} a={a} />)}
          </div>
        )}

        {data.pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="annonces-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="annonces-prev">Précédent</Button>
            <span className="text-sm text-slate-600">Page {data.page} / {data.pages}</span>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="annonces-next">Suivant</Button>
          </div>
        )}
      </div>
    </div>
  );
}
